import { motion } from "framer-motion";
import { Reveal } from "@/lib/motionPrimitives";
import { Check } from "lucide-react";
import { STATS } from "@/data/site";
import Counter from "./Counter";

const SPECIALTIES = [
  "Industrial Structural Painting",
  "Protective Coatings",
  "Corrosion Protection",
  "Surface Preparation",
  "Rust Removal",
  "Factory Maintenance Painting",
  "Interior Painting",
  "Exterior Painting",
  "Retrofitting",
  "Non Destructive Testing (NDT)",
  "Vibration Control",
];

const CHAPTERS = [
  {
    no: "01",
    title: "Who We Are",
    body: "Agraj Enterprise is a trusted provider of industrial structural painting and protective coating solutions, delivering reliable services for factories, warehouses, process plants and heavy industrial structures.",
  },
  {
    no: "02",
    title: "What We Do",
    body: "We specialise in surface preparation, rust removal, corrosion protection and industrial coating applications — executed in demanding environments including PPA and Caustic Soda plants where durability is critical.",
  },
  {
    no: "03",
    title: "Why It Matters",
    body: "We don't just paint structures — we protect your assets. Every project is engineered for durability, delivered cost-effectively and completed on time.",
  },
];

const CREDS = [
  "7+ years experience",
  "Skilled manpower",
  "Quality workmanship",
  "Timely project completion",
  "Cost-effective solutions",
];

export default function About() {
  return (
    <section id="about" className="relative py-24 md:py-32" data-testid="about">
      <div className="container-x">
        <Reveal>
          <p className="overline">About Agraj Enterprise</p>
          <h2 className="mt-4 max-w-4xl font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            We don&apos;t just paint structures — <span className="text-primary">we protect your assets.</span>
          </h2>
        </Reveal>

        {/* Mobile stats */}
        <div className="mt-10 grid grid-cols-2 gap-4 md:hidden">
          {STATS.map((s) => (
            <div key={s.label} className="rounded-2xl border border-border bg-surface p-5 text-center">
              <div className="font-display text-3xl font-black text-primary">
                <Counter to={s.value} suffix={s.suffix} />
              </div>
              <div className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="mt-16 grid gap-12 lg:grid-cols-12">
          {/* Manifesto chapters */}
          <div className="lg:col-span-7 space-y-10">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.no} delay={i * 0.08}>
                <div className="flex gap-6 border-b border-border pb-8">
                  <span className="font-display text-4xl md:text-5xl font-black text-primary/25">{c.no}</span>
                  <div>
                    <h3 className="font-display text-xl md:text-2xl font-semibold">{c.title}</h3>
                    <p className="mt-3 text-muted-foreground leading-relaxed">{c.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Specialties + creds */}
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-border bg-surface p-8">
                <h3 className="font-display text-lg font-semibold uppercase tracking-wide">Our Specialities</h3>
                <div className="mt-5 grid gap-3">
                  {SPECIALTIES.map((s) => (
                    <div key={s} className="flex items-center gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Check className="h-3.5 w-3.5" />
                      </span>
                      <span className="text-sm font-medium">{s}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <div className="mt-6 flex flex-wrap gap-2">
              {CREDS.map((c, i) => (
                <motion.span
                  key={c}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="rounded-full border border-border px-4 py-2 text-xs font-medium text-muted-foreground"
                >
                  {c}
                </motion.span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
